import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import {AddLink, ArrowBack, ArrowForward, Compress, DeleteOutline, KeyboardArrowDown, LinkOff} from "@mui/icons-material";
import {Timestamp} from "firebase/firestore";
import {getDownloadURL, ref, uploadBytesResumable} from "firebase/storage";
import firebaseService from "../../../services/firebaseService";
import dateCardTemplateService from "../../../services/dateCardTemplateService";
import {IDateCardTemplate, TemplateGender} from "../../../types/DateCardTemplate";
import {useConfirmDialog} from "../../../hooks/useConfirmDialog";
import {ReorderCategoriesModal} from "./ReorderCategoriesModal";

type GenderFilter = TemplateGender;

function base64ToBlob(base64: string): Promise<Blob> {
    return fetch(base64).then((r) => r.blob());
}

function recompressBase64(base64: string): Promise<string> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext('2d')!.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/jpeg', 0.17));
        };
        img.src = base64;
    });
}

export const DateCardDesignsList: React.FC = () => {
    const navigate = useNavigate();
    const {openConfirmDialog} = useConfirmDialog();
    const [templates, setTemplates] = useState<IDateCardTemplate[]>([]);
    const [categoryOrder, setCategoryOrder] = useState<string[]>([]);
    const [genderFilter, setGenderFilter] = useState<GenderFilter>('men');
    const [reorderOpen, setReorderOpen] = useState(false);
    const [linkingFromId, setLinkingFromId] = useState<string | null>(null);
    const [linkingSelected, setLinkingSelected] = useState<Set<string>>(new Set());
    const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
    const [compressingId, setCompressingId] = useState<string | null>(null);
    const stripRefs = useRef<Record<string, HTMLDivElement | null>>({});

    function scrollStrip(parentId: string, direction: -1 | 1) {
        const el = stripRefs.current[parentId];
        if (!el) return;
        el.scrollBy({left: direction * (el.clientWidth / 3), behavior: 'smooth'});
    }

    async function loadData() {
        const [all, order] = await Promise.all([
            dateCardTemplateService.getAll(),
            dateCardTemplateService.getCategories(),
        ]);
        setTemplates(all);

        const knownCats = new Set(order);
        const extraCats = [...new Set(all.map((t) => t.category))].filter(
            (c) => !knownCats.has(c)
        );
        setCategoryOrder([...order, ...extraCats]);
    }

    useEffect(() => {
        void loadData();
    }, []);

    function deleteTemplate(id?: string) {
        if (!id) return;
        if ((variantsByParent[id] ?? []).length > 0) return;
        openConfirmDialog(async () => {
            await dateCardTemplateService.delete(id);
            void loadData();
        });
    }

    async function unlinkVariant(id: string) {
        await dateCardTemplateService.unlinkVariant(id);
        void loadData();
    }

    function togglePaid(template: IDateCardTemplate) {
        if (!template.id) return;
        const nextIsPaid = !template.isPaid;
        // Marking a main card also marks all of its variants (and vice versa when unmarking).
        const variantIds = templates
            .filter((t) => t.parentCardId === template.id)
            .map((t) => t.id!)
            .filter(Boolean);
        const affectedIds = new Set([template.id, ...variantIds]);

        setTemplates((prev) =>
            prev.map((t) => (t.id && affectedIds.has(t.id) ? {...t, isPaid: nextIsPaid} : t))
        );
        void dateCardTemplateService.updatePaidFlagForGroup(template.id, variantIds, nextIsPaid);
    }

    function renderPaidToggle(template: IDateCardTemplate, size = 22) {
        const isPaid = !!template.isPaid;
        return (
            <Box
                onClick={(e) => { e.stopPropagation(); togglePaid(template); }}
                title={isPaid ? 'Paid design (click to make free)' : 'Free design (click to mark as paid)'}
                sx={{
                    width: size,
                    height: size,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    border: isPaid ? '1.5px solid white' : '1.5px solid rgba(255,255,255,0.4)',
                    bgcolor: isPaid ? '#f5b400' : 'rgba(0,0,0,0.35)',
                    color: isPaid ? 'white' : 'rgba(255,255,255,0.5)',
                    fontSize: size * 0.6,
                    fontWeight: 700,
                    lineHeight: 1,
                    userSelect: 'none',
                }}
            >
                $
            </Box>
        );
    }

    function moveCard(group: IDateCardTemplate[], index: number, direction: -1 | 1) {
        const swapIndex = index + direction;
        const reordered = [...group];
        [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];

        // Normalize all order values to sequential indices so duplicates never cause a no-op
        const updates = reordered
            .filter((t) => t.id)
            .map((t, i) => ({id: t.id!, order: i}));

        setTemplates((prev) =>
            prev.map((t) => {
                const u = updates.find((u) => u.id === t.id);
                return u ? {...t, order: u.order} : t;
            })
        );

        void dateCardTemplateService.updateTemplateOrders(updates);
    }

    async function demoteMainCard(mainTemplate: IDateCardTemplate, variants: IDateCardTemplate[]) {
        if (!mainTemplate.id || variants.length === 0) return;
        const [newMain, ...rest] = variants; // already sorted ascending by order
        if (!newMain.id) return;
        await dateCardTemplateService.promoteVariantToMain(
            mainTemplate.id,
            newMain.id,
            mainTemplate.order,
            rest.filter((v) => !!v.id).map((v) => v.id!)
        );
        void loadData();
    }

    async function generateThumbURL(template: IDateCardTemplate) {
        if (!template.thumb || !template.id || template.thumbURL) return;
        setCompressingId(template.id);
        try {
            const blob = await base64ToBlob(template.thumb);
            const thumbURL = await new Promise<string>((resolve, reject) => {
                const storageRef = ref(
                    firebaseService.storage,
                    `/DateCardTemplateImages/thumbs/${Timestamp.now().seconds}.jpg`,
                );
                const uploadTask = uploadBytesResumable(storageRef, blob);
                uploadTask.on('state_changed', () => {}, reject, async () => {
                    resolve(await getDownloadURL(uploadTask.snapshot.ref));
                });
            });
            const newThumb = await recompressBase64(template.thumb);
            await dateCardTemplateService.updateThumbFields(template.id, newThumb, thumbURL);
            void loadData();
        } finally {
            setCompressingId(null);
        }
    }

    async function saveLinking() {
        if (!linkingFromId || linkingSelected.size === 0) return;
        await dateCardTemplateService.linkVariants(linkingFromId, [...linkingSelected]);
        setLinkingFromId(null);
        setLinkingSelected(new Set());
        void loadData();
    }

    function cancelLinking() {
        setLinkingFromId(null);
        setLinkingSelected(new Set());
    }

    function toggleLinkingSelected(id: string) {
        setLinkingSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    const rootTemplates = templates.filter((t) => !t.parentCardId);
    const variantsByParent: Record<string, IDateCardTemplate[]> = {};
    for (const t of templates) {
        if (t.parentCardId) {
            (variantsByParent[t.parentCardId] ??= []).push(t);
        }
    }
    const anchorCard = linkingFromId ? templates.find((t) => t.id === linkingFromId) : null;

    function renderCardGroup(group: IDateCardTemplate[]) {
        const sorted = [...group].sort((a, b) => a.order - b.order);
        return sorted.map((template, i) => {
            const variants = (variantsByParent[template.id!] ?? []).sort((a, b) => a.order - b.order);
            const isLinkingAnchor = template.id === linkingFromId;
            const isAlreadyLinked = !!template.parentCardId;
            const isAlreadyParent = (variantsByParent[template.id!] ?? []).length > 0;
            const canBeSelected =
                linkingFromId !== null &&
                !isLinkingAnchor &&
                !isAlreadyLinked &&
                !isAlreadyParent &&
                template.category === anchorCard?.category;
            const hasVariants = variants.length > 0;

            return (
                <Box
                    key={template.id}
                    sx={{
                        borderRadius: 1,
                        overflow: 'hidden',
                        opacity: linkingFromId && !isLinkingAnchor && !canBeSelected ? 0.4 : 1,
                    }}
                >
                    {/* Main image */}
                    <Box sx={{position: 'relative'}}>
                        <img
                            src={template.thumb ?? template.image}
                            alt={template.name}
                            loading={'lazy'}
                            style={{display: 'block', width: '100%'}}
                        />
                        {canBeSelected && (
                            <Box
                                sx={{position: 'absolute', top: 4, left: 4, cursor: 'pointer'}}
                                onClick={() => toggleLinkingSelected(template.id!)}
                            >
                                <Checkbox
                                    checked={linkingSelected.has(template.id!)}
                                    sx={{
                                        color: 'white',
                                        '&.Mui-checked': {color: 'white'},
                                        bgcolor: 'rgba(0,0,0,0.4)',
                                        borderRadius: 1,
                                        p: 0.5,
                                    }}
                                />
                            </Box>
                        )}
                        {!linkingFromId && (
                            <Box sx={{position: 'absolute', top: 8, left: 8}}>
                                {renderPaidToggle(template)}
                            </Box>
                        )}
                    </Box>

                    {/* Action bar */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center',
                        bgcolor: 'rgba(0,0,0,0.6)', px: 1, minHeight: 40,
                    }}>
                        <Typography
                            variant={'caption'}
                            sx={{flex: 1, color: 'white', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                        >
                            {template.name}
                        </Typography>
                        <Box sx={{display: 'flex', alignItems: 'center', flexShrink: 0}}>
                            {!template.thumbURL && template.thumb && (
                                <IconButton
                                    size={'small'}
                                    sx={{color: 'rgba(255,255,255,0.8)'}}
                                    disabled={compressingId === template.id}
                                    onClick={() => void generateThumbURL(template)}
                                >
                                    {compressingId === template.id
                                        ? <CircularProgress size={14} sx={{color: 'white'}}/>
                                        : <Compress fontSize={'small'}/>}
                                </IconButton>
                            )}
                            <IconButton
                                size={'small'}
                                sx={{color: 'rgba(255,255,255,0.8)'}}
                                disabled={i === 0}
                                onClick={() => moveCard(sorted, i, -1)}
                            >
                                <ArrowBack fontSize={'small'}/>
                            </IconButton>
                            <IconButton
                                size={'small'}
                                disabled={isAlreadyParent}
                                sx={{color: 'rgba(255,255,255,0.8)'}}
                                onClick={() => deleteTemplate(template.id)}
                            >
                                <DeleteOutline fontSize={'small'}/>
                            </IconButton>
                            {!linkingFromId && (
                                <IconButton
                                    size={'small'}
                                    sx={{color: 'rgba(255,255,255,0.8)'}}
                                    onClick={() => {
                                        setLinkingFromId(template.id!);
                                        setLinkingSelected(new Set());
                                    }}
                                >
                                    <AddLink fontSize={'small'}/>
                                </IconButton>
                            )}
                            <IconButton
                                size={'small'}
                                sx={{color: 'rgba(255,255,255,0.8)'}}
                                disabled={i === sorted.length - 1}
                                onClick={() => moveCard(sorted, i, 1)}
                            >
                                <ArrowForward fontSize={'small'}/>
                            </IconButton>
                            <IconButton
                                size={'small'}
                                sx={{color: 'rgba(255,255,255,0.8)'}}
                                disabled={!hasVariants}
                                onClick={() => void demoteMainCard(template, variants)}
                            >
                                <KeyboardArrowDown fontSize={'small'}/>
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Variant strip */}
                    {hasVariants && (
                        <Box
                            sx={{
                                px: 1, pb: 1, pt: 0.5,
                                cursor: 'pointer',
                                bgcolor: 'rgba(0,0,0,0.7)',
                            }}
                            onClick={() => setExpandedGroupId(
                                expandedGroupId === template.id ? null : template.id!
                            )}
                        >
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            {variants.length > 3 && (
                                <IconButton
                                    size={'small'}
                                    sx={{color: 'white', flexShrink: 0, p: 0.25}}
                                    onClick={(e) => { e.stopPropagation(); scrollStrip(template.id!, -1); }}
                                >
                                    <ArrowBack sx={{fontSize: 14}}/>
                                </IconButton>
                            )}
                            <Box
                                ref={(el) => { stripRefs.current[template.id!] = el as HTMLDivElement; }}
                                sx={{
                                    flex: 1,
                                    display: 'flex', gap: 0.5,
                                    overflow: 'hidden',
                                }}
                            >
                                {variants.map((variant, vi) => (
                                    <Box key={variant.id} sx={{position: 'relative', flex: '0 0 calc((100% - 8px) / 3)', minWidth: 0}}>
                                        <img
                                            src={variant.thumb ?? variant.image}
                                            alt={variant.name}
                                            style={{
                                                width: '100%',
                                                maxHeight: '8vw',
                                                objectFit: 'contain',
                                                borderRadius: 4,
                                                display: 'block',
                                            }}
                                        />
                                        <Box sx={{position: 'absolute', top: 4, left: 4}}>
                                            {renderPaidToggle(variant, 16)}
                                        </Box>
                                        {!variant.thumbURL && variant.thumb && (
                                            <IconButton
                                                size={'small'}
                                                sx={{
                                                    position: 'absolute', top: 4, left: 24,
                                                    color: 'white', bgcolor: 'rgba(0,0,0,0.5)', p: 0.25,
                                                    '&:hover': {bgcolor: 'rgba(0,0,0,0.75)'},
                                                }}
                                                disabled={compressingId === variant.id}
                                                onClick={(e) => { e.stopPropagation(); void generateThumbURL(variant); }}
                                            >
                                                {compressingId === variant.id
                                                    ? <CircularProgress size={10} sx={{color: 'white'}}/>
                                                    : <Compress sx={{fontSize: 12}}/>}
                                            </IconButton>
                                        )}
                                        {expandedGroupId === template.id && (
                                            <>
                                                {/* Unlink — top-right */}
                                                <IconButton
                                                    size={'small'}
                                                    sx={{
                                                        position: 'absolute', top: 2, right: 2,
                                                        color: 'white', bgcolor: 'rgba(0,0,0,0.5)', p: 0.25,
                                                        '&:hover': {bgcolor: 'rgba(0,0,0,0.75)'},
                                                    }}
                                                    onClick={(e) => { e.stopPropagation(); void unlinkVariant(variant.id!); }}
                                                >
                                                    <LinkOff sx={{fontSize: 12}}/>
                                                </IconButton>
                                                {/* Delete — bottom-right */}
                                                <IconButton
                                                    size={'small'}
                                                    sx={{
                                                        position: 'absolute', bottom: 24, right: 2,
                                                        color: 'white', bgcolor: 'rgba(0,0,0,0.5)', p: 0.25,
                                                        '&:hover': {bgcolor: 'rgba(180,0,0,0.75)'},
                                                    }}
                                                    onClick={(e) => { e.stopPropagation(); deleteTemplate(variant.id); }}
                                                >
                                                    <DeleteOutline sx={{fontSize: 12}}/>
                                                </IconButton>
                                                {/* Reorder arrows — below image */}
                                                <Box sx={{display: 'flex', justifyContent: 'center', gap: 0.25, mt: 0.25}}>
                                                    <IconButton
                                                        size={'small'}
                                                        disabled={vi === 0}
                                                        sx={{color: 'white', p: 0.25}}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            moveCard(variants, vi, -1);
                                                        }}
                                                    >
                                                        <ArrowBack sx={{fontSize: 12}}/>
                                                    </IconButton>
                                                    <IconButton
                                                        size={'small'}
                                                        disabled={vi === variants.length - 1}
                                                        sx={{color: 'white', p: 0.25}}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            moveCard(variants, vi, 1);
                                                        }}
                                                    >
                                                        <ArrowForward sx={{fontSize: 12}}/>
                                                    </IconButton>
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                            {variants.length > 3 && (
                                <IconButton
                                    size={'small'}
                                    sx={{color: 'white', flexShrink: 0, p: 0.25}}
                                    onClick={(e) => { e.stopPropagation(); scrollStrip(template.id!, 1); }}
                                >
                                    <ArrowForward sx={{fontSize: 14}}/>
                                </IconButton>
                            )}
                        </Box>
                        </Box>
                    )}
                </Box>
            );
        });
    }

    const grouped: Record<string, IDateCardTemplate[]> = {};
    for (const cat of categoryOrder) {
        grouped[cat] = rootTemplates.filter((t) => t.category === cat);
    }

    return (
        <Box>
            <Box sx={{display: 'flex', alignItems: 'center', mb: 3, gap: 2}}>
                <Typography variant={'h1'} sx={{flexGrow: 1}}>Date Card Designs</Typography>
                <Button variant={'outlined'} onClick={() => setReorderOpen(true)}>
                    Reorder Categories
                </Button>
                <Button variant={'contained'} onClick={() => navigate('/date-card-designs/add')}>
                    Add Design
                </Button>
            </Box>

            <ToggleButtonGroup
                exclusive
                value={genderFilter}
                onChange={(_, v) => v && setGenderFilter(v)}
                sx={{mb: 4}}
            >
                <ToggleButton value={'men'}>Men</ToggleButton>
                <ToggleButton value={'women'}>Women</ToggleButton>
            </ToggleButtonGroup>

            {categoryOrder.length === 0 && (
                <Typography color={'text.secondary'}>No designs available.</Typography>
            )}

            {categoryOrder.map((cat) => {
                const allInCat = grouped[cat] ?? [];
                const group = allInCat.filter((t) => t.gender === genderFilter);

                if (group.length === 0) return null;

                return (
                    <Box key={cat} sx={{mb: 5}}>
                        <Typography variant={'h2'} sx={{mb: 2}}>{cat}</Typography>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: '12px',
                            alignItems: 'start',
                        }}>
                            {renderCardGroup(group)}
                        </Box>
                    </Box>
                );
            })}

            {linkingFromId && (
                <Box sx={{
                    position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
                    display: 'flex', gap: 2, bgcolor: 'background.paper', p: 2,
                    borderRadius: 2, boxShadow: 4, zIndex: 1200,
                }}>
                    <Button onClick={cancelLinking}>Cancel</Button>
                    <Button
                        variant={'contained'}
                        disabled={linkingSelected.size === 0}
                        onClick={() => void saveLinking()}
                    >
                        Link {linkingSelected.size} card{linkingSelected.size !== 1 ? 's' : ''}
                    </Button>
                </Box>
            )}

            <ReorderCategoriesModal
                open={reorderOpen}
                categories={categoryOrder}
                templates={templates}
                onClose={() => setReorderOpen(false)}
                onSaved={(newOrder) => {
                    setCategoryOrder(newOrder);
                    setReorderOpen(false);
                }}
            />
        </Box>
    );
};
